<template>
  <scoped>
    <OrderToolbar
      v-if="orderEnumerationResponse"
      v-model:value="orderInput"
      :orderOrders="orderEnumerationResponse.orders"
      :orderStatuses="orderEnumerationResponse.statuses"
      :orderTypes="['PURCHASE_CREDIT', 'PURCHASE_DEBIT', 'PURCHASE_OFFER']"
      @update="update()"
      @reset="reset()"
    />
    <OrderList
      v-if="orderResponse"
      v-model:selectedIndex="selectedIndex"
      :items="orderResponse.records"
      @select="select()"
    />
    <Paginator
      v-if="orderResponse"
      v-model:currentPage="orderInput.plot.page"
      :itemTotal="orderResponse.recordsTotal"
      :pageItems="orderInput.plot.items"
      @update="update()"
    />
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import OrderList from "@/components/lists/order.vue"
  import OrderToolbar from "@/components/toolbars/order.vue"
  import {nextTick, onBeforeMount, PropType, ref} from "vue"
  import {
    Order,
    OrderEnumerationResponse,
    OrderInput,
    OrderResponse
  } from "@/endpoints/rutsolve/gql/generated/graphql"
  import Paginator from "@/components/paginator.vue"
  import {Order as OrderResolver} from "@/endpoints/rutsolve/gql/resolvers/order"
  import {Enumeration as EnumerationResolver} from "@/endpoints/rutsolve/gql/resolvers/enumeration"
  import {PurchaseOrderSelector as Storage} from "@/storage/purchase_order_selector"
  import ErrorModal from "@/components/modals/error.vue"

  const value = defineModel("value", {
    default: null,
    required: false,
    type: Object as PropType<Order>
  })

  const emits = defineEmits([
    "select",
  ])

  const error = ref<Error>(null)
  const orderResolver = new OrderResolver()
  const enumerationResolver = new EnumerationResolver()
  const orderResponse = ref<OrderResponse>(null)
  const orderEnumerationResponse = ref<OrderEnumerationResponse>(null)
  const orderInput = ref<OrderInput>(null)
  const selectedIndex = ref<number>(null)

  onBeforeMount( () => {
    read()
  })

  function update() {
    Storage.writeInput(orderInput.value)
    read()
  }

  async function select() {
    const selectedOrder = orderResponse.value.records[selectedIndex.value]
    value.value = selectedOrder
    Storage.writeOrderID(selectedOrder.id)
    await nextTick()
    emits("select")
  }

  function reset() {
    Storage.deleteInput()
    Storage.deleteOrderID()
    selectedIndex.value = null
    value.value = null
    read()
  }

  async function read() {
    try {
      orderEnumerationResponse.value = await enumerationResolver.readOrderEnumerations()
      const storedOrderInput = Storage.readInput()
      orderInput.value = structuredClone(storedOrderInput)
      orderResponse.value = await orderResolver.selector(storedOrderInput)
      if (!orderResponse.value || !orderResponse.value.records) {
        return
      }
      const storedSelectedOrderID = Storage.readOrderID()
      for (const [index, order] of orderResponse.value.records.entries()) {
        if (order.id == storedSelectedOrderID) {
          selectedIndex.value = index
          value.value = order
          break
        }
      }
    } catch(e) {
      error.value = e
    }
  }
</script>
